import { Roi, RoiContours } from '../dicom/structure-set';
import { TracedSlice } from './webgl/sdf/sdf';
import { ViewerState } from './viewer-state';

type ContoursAsSavedCache = {
    editKey: any[],
    contours: RoiContours,
    /** Traced edited slices, reused as long as their pixels don't change (see Sdf.traceContours) */
    tracedSlices: Map<string, TracedSlice>,
};

const caches = new WeakMap<Roi, ContoursAsSavedCache>();

/**
 * Contours of an ROI as they would be saved right now, without saving or changing anything: the saved contour data,
 * with the slices edited since the last save traced from the ROI's SDF (see Sdf.traceContours).
 *
 * Tracing is slow and brushing marks all slices of the ROI as edited, so the result is cached until the ROI is edited
 * again, and after an edit only slices whose pixels actually changed are traced again.
 */
export function getContoursAsTheyWouldBeSaved(roi: Roi, vs: ViewerState): RoiContours {
    const savedContours = roi.structureSet.contourData[roi.roiNumber] || {};
    const editedSliceIds = roi.contoursChangedInAllSlices ? vs.image.sliceIds : roi.contoursChangedInSlices;
    if (!roi.sdf || editedSliceIds.length === 0) { return savedContours; }

    const editKey = getRoiEditKey(roi, vs);
    const cache = caches.get(roi);
    if (cache && cache.editKey.every((key, i) => key === editKey[i])) { return cache.contours; }

    const tracedSlices = cache ? cache.tracedSlices : new Map<string, TracedSlice>();
    const contours = { ...savedContours, ...roi.sdf.traceContours(editedSliceIds, tracedSlices) };
    caches.set(roi, { editKey, contours, tracedSlices });
    return contours;
}

/** Values that change whenever the ROI is edited, undone, redone or saved */
function getRoiEditKey(roi: Roi, vs: ViewerState): any[] {
    const undoStates = vs.undoStack.undoStack;
    return [
        roi.sdf,
        roi.structureSet.contourData[roi.roiNumber],
        roi.contoursChangedInAllSlices,
        roi.contoursChangedInSlices.join('\n'),
        // Every edit pushes a new state here first, also edits that modify the SDF in place
        undoStates[undoStates.length - 1],
    ];
}
